import { useCallback, useEffect, useRef, useState } from 'react';

/** Andrea's design (deck of 18/09, slide 7): ten seconds, then the chat opens by itself. */
export const INTERVIEW_COUNTDOWN_SECONDS = 10;

/**
 * The countdown on the returning worker's confirmation, which has no X: after
 * `INTERVIEW_COUNTDOWN_SECONDS` it calls `onElapsed` once, and never after the component that
 * owns it has gone.
 *
 * The three ways a timer like this does damage, and where each is handled:
 *   - navigating twice, because the worker pressed the button and the timer fired anyway:
 *     `stop()` marks it settled AND clears the interval, so the firing effect below is skipped.
 *   - navigating a page that is no longer mounted: the cleanup clears the interval, so no tick
 *     ever lands, `remaining` never reaches 0 and `onElapsed` is never reached. The cleanup
 *     deliberately does NOT set `settledRef` — that would make a remount (React StrictMode
 *     double-invokes effects in development) a countdown that can never fire.
 *   - restarting on every re-render: the interval is created by an effect with no dependencies,
 *     so it is created once per mount. `onElapsed` is read through a ref instead of being a
 *     dependency, which is what lets the caller pass an inline arrow without resetting the clock.
 */
export const useInterviewCountdown = (onElapsed: () => void) => {
  const [remaining, setRemaining] = useState(INTERVIEW_COUNTDOWN_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const settledRef = useRef(false);

  // Kept in a ref so a new `onElapsed` identity on each render does not re-run the effect that
  // owns the interval. Always the latest one by the time it is called.
  const onElapsedRef = useRef(onElapsed);
  useEffect(() => {
    onElapsedRef.current = onElapsed;
  });

  const clear = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = undefined;
  }, []);

  /** The worker got there first: no navigation from the timer any more. */
  const stop = useCallback(() => {
    settledRef.current = true;
    clear();
  }, [clear]);

  useEffect(() => {
    intervalRef.current = setInterval(() => setRemaining((left) => (left > 0 ? left - 1 : 0)), 1000);
    return clear;
  }, [clear]);

  useEffect(() => {
    if (remaining > 0 || settledRef.current) return;
    stop();
    onElapsedRef.current();
  }, [remaining, stop]);

  return { remaining, stop };
};
